import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectClustersType } from 'shared/map-search/selectors/clustersType';
import { selectActiveFeature } from 'shared/map-search/selectors/features';
import { EClustersType } from 'shared/map-search/types/clustersType';
import { TFeature } from 'shared/map-search/types/map';

import { useFeaturesContext } from '../../context';

interface IUseBalloonHandlersProps {
  onFeatureMouseEnter: (feature: TFeature) => void;
  onFeatureMouseLeave: () => void;
  hoveredFeatureRef: React.RefObject<TFeature | null>;
}

export const useBalloonHandlers = ({
  hoveredFeatureRef,
  onFeatureMouseEnter,
  onFeatureMouseLeave,
}: IUseBalloonHandlersProps) => {
  const { balloonManager } = useFeaturesContext();

  const clustersType = useSelector(selectClustersType);
  const selectedFeature = useSelector(selectActiveFeature);

  const lastSelectedFeatureRef = useRef<TFeature | null>(selectedFeature);

  const handleBalloonMouseEnter = useCallback(
    (event: YMaps.IEvent) => {
      const featureByBalloon = balloonManager.objectManager.objects.getById<TFeature>(event.get<string>('objectId'));
      const lastSelectedFeature = lastSelectedFeatureRef.current;

      if (!featureByBalloon || lastSelectedFeature?.id === featureByBalloon.id) {
        return;
      }

      onFeatureMouseEnter(featureByBalloon);

      balloonManager.updateBalloonProperties(featureByBalloon.id, {
        ...featureByBalloon.properties,
        isHovered: true,
      });
    },
    [balloonManager, onFeatureMouseEnter],
  );

  const handleBalloonMouseLeave = useCallback(() => {
    const hoveredFeature = hoveredFeatureRef.current;

    if (!hoveredFeature) {
      return;
    }

    onFeatureMouseLeave();

    balloonManager.updateBalloonProperties(hoveredFeature.id, {
      ...hoveredFeature.properties,
      isHovered: false,
    });
  }, [balloonManager, onFeatureMouseLeave, hoveredFeatureRef]);

  useEffect(() => {
    if (!clustersType) {
      return () => {};
    }

    balloonManager.objectManager.objects.events.add('mouseenter', handleBalloonMouseEnter);
    balloonManager.objectManager.objects.events.add('mouseleave', handleBalloonMouseLeave);

    return () => {
      balloonManager.objectManager.objects.events.remove('mouseenter', handleBalloonMouseEnter);
      balloonManager.objectManager.objects.events.remove('mouseleave', handleBalloonMouseLeave);
    };
  }, [clustersType, balloonManager, handleBalloonMouseEnter, handleBalloonMouseLeave]);

  useEffect(() => {
    if (!selectedFeature || clustersType !== EClustersType.OpenablePeas) {
      return;
    }

    const lastSelectedFeature = lastSelectedFeatureRef.current;

    if (
      lastSelectedFeature &&
      lastSelectedFeature.id !== selectedFeature.id &&
      balloonManager.objectManager.objects.getById<TFeature>(lastSelectedFeature.id)
    ) {
      balloonManager.objectManager.remove([lastSelectedFeature.id]);
    }

    lastSelectedFeatureRef.current = selectedFeature;
  }, [selectedFeature, clustersType, balloonManager]);
};
